import { CoordinateType } from 'common/types.ts';

type TLatLng = { lat: number; lng: number };
type TCoordinateArray = [number, number];
type Coordinates = CoordinateType | TLatLng | TCoordinateArray | undefined;

export const isValidCoordinate = (coordinate: Coordinates) => {
  const lat =
    (coordinate as unknown as CoordinateType)?.latitude ||
    (coordinate as unknown as TLatLng)?.lat ||
    (coordinate as unknown as TCoordinateArray)?.[0];
  const lng =
    (coordinate as unknown as CoordinateType)?.longitude ||
    (coordinate as unknown as TLatLng)?.lng ||
    (coordinate as unknown as TCoordinateArray)?.[1];
  return coordinate && typeof lat === 'number' && typeof lng === 'number';
};

export const areCoordinatesTheSame = (coordinate1: Coordinates, coordinate2: Coordinates) => {
  const lat1 =
    (coordinate1 as unknown as CoordinateType)?.latitude ||
    (coordinate1 as unknown as TLatLng)?.lat ||
    (coordinate1 as unknown as TCoordinateArray)?.[0];
  const lat2 =
    (coordinate2 as unknown as CoordinateType)?.latitude ||
    (coordinate2 as unknown as TLatLng)?.lat ||
    (coordinate2 as unknown as TCoordinateArray)?.[0];
  const lng1 =
    (coordinate1 as unknown as CoordinateType)?.longitude ||
    (coordinate1 as unknown as TLatLng)?.lng ||
    (coordinate1 as unknown as TCoordinateArray)?.[1];
  const lng2 =
    (coordinate2 as unknown as CoordinateType)?.longitude ||
    (coordinate2 as unknown as TLatLng)?.lng ||
    (coordinate2 as unknown as TCoordinateArray)?.[1];
  return coordinate1 && coordinate2 && lat1 === lat2 && lng1 === lng2;
};

export const convertLatLngToCoordinateArray = (center: CoordinateType | undefined): TCoordinateArray | undefined => {
  if (isValidCoordinate(center)) return [Number(center!.longitude), Number(center!.latitude)];
  return undefined;
};

export const convertCoordinatesToLatLng = (center: CoordinateType | undefined): TLatLng | undefined => {
  if (isValidCoordinate(center)) return { lat: Number(center!.latitude), lng: Number(center!.longitude) };
  return undefined;
};

export const convertLatLngToCoordinates = (center: TLatLng | undefined): CoordinateType | undefined => {
  if (isValidCoordinate(center)) return { latitude: center!.lat, longitude: center!.lng };
  return undefined;
};

export const getCoordinatesDistance = (a: CoordinateType, b: CoordinateType): number => {
  // Radius of the Earth in meters (mean radius)
  const R = 6371000; // meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);

  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));

  // Distance in meters
  return R * c;
};
